import { useCallback, useMemo, useState } from 'react'
import { FontSize, FontWeight } from '../../../../styles/commonStyles'
import {
  Avatar,
  Box,
  ClickAwayListener,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popper,
  Typography,
} from '@mui/material'
import { Button } from '../../buttons'
import { colorPalette } from '../../../style/colorPalette'
import { intl } from '../../../../i18n'
import styled from 'styled-components'
import { useProjectMemberRepository } from '../../../../services/adaptors/projectMemberRepositoryAdaptor'
import { SelectOptionWithIcon } from '../../select/model'
import { UserDetail } from '../../../../lib/functions/user'

type Props = {
  initialMessage: string
  message: string
  selectedMember: SelectOptionWithIcon<string> | undefined
  setSelectedMember: (member: SelectOptionWithIcon<string>) => void
  projectUuid: string
  loginUser: UserDetail
  height?: number
  offset: number
}

export const ProxyProjectMemberSelector = ({
  initialMessage,
  message,
  selectedMember,
  setSelectedMember,
  projectUuid,
  loginUser,
  height,
  offset,
}: Props) => {
  const isUserSelected = useMemo(() => !!selectedMember, [selectedMember])
  const [open, setOpen] = useState<boolean>(false)
  const [anchorEl, setAnchorEl] = useState<any>(null)
  const { fetchListAsReferencedEntity: searchProjectMember } =
    useProjectMemberRepository()

  const [projectMembers, setProjectMembers] = useState<
    SelectOptionWithIcon<string>[]
  >([])

  const fetchMemberOptions = useCallback(async () => {
    const response = await searchProjectMember(projectUuid)
    const users = response
      .filter(user => user.uuid !== loginUser.uuid)
      .map(user => ({
        value: user.uuid,
        name: user.name,
        iconUrl: user.iconUrl,
      }))
    setProjectMembers(users)
    return users
  }, [])

  return (
    <ProjectMemberSelector>
      <ProjectMemberSelectBox $isUserSelected={isUserSelected}>
        <SelectedProjectMemberDiv $isUserSelected={isUserSelected}>
          {isUserSelected ? (
            <SelectedMemberAvatar
              variant="circular"
              src={selectedMember?.iconUrl}
            />
          ) : null}
          <SelectedMemberNameTypography>
            {isUserSelected ? selectedMember?.name : null}
          </SelectedMemberNameTypography>
          <Typography>{isUserSelected ? message : initialMessage}</Typography>
        </SelectedProjectMemberDiv>
        <ProjectMemberSelectButton
          colorPattern={isUserSelected ? 'monotone' : 'skyBlue'}
          variant={isUserSelected ? 'outlined' : undefined}
          onClick={e => {
            setOpen(true)
            setAnchorEl(e.target)
            fetchMemberOptions()
          }}
        >
          {isUserSelected
            ? intl.formatMessage({
                id: 'taskActualWork.proxy.user.change',
              })
            : intl.formatMessage({
                id: 'taskActualWork.proxy.user.select',
              })}
        </ProjectMemberSelectButton>
        <ProjectMemberSelectPopper
          open={open}
          anchorEl={anchorEl}
          placement="bottom-start"
          modifiers={[{ name: 'offset', options: { offset: [0, 20] } }]}
        >
          <ClickAwayListener onClickAway={() => setOpen(false)}>
            <ProjectMemberListContainer $height={height ?? 0} $offset={offset}>
              <List>
                {projectMembers.map(projectMember => (
                  <ListItem key={projectMember.value}>
                    <ListItemButton
                      onClick={() => {
                        setSelectedMember(projectMember)
                        setOpen(false)
                      }}
                    >
                      <ProjectMemberAvatar
                        variant="circular"
                        src={projectMember.iconUrl}
                      />
                      <ListItemText>{projectMember.name}</ListItemText>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </ProjectMemberListContainer>
          </ClickAwayListener>
        </ProjectMemberSelectPopper>
      </ProjectMemberSelectBox>
    </ProjectMemberSelector>
  )
}

const ProjectMemberAvatar = styled(Avatar)({
  margin: '0px 15px 0px 5px',
  width: 20,
  height: 20,
})

const ProjectMemberListContainer = styled(Box)<{
  $height: number
  $offset: number
}>(({ $height, $offset }) => ({
  maxHeight: $height ? $height - $offset : 0,
  height: 'fit-content',
  overflowY: 'auto',
}))

const ProjectMemberSelectPopper = styled(Popper)({
  backgroundColor: colorPalette.monotone[0],
  boxShadow:
    'rgb(0 0 0 / 20%) 0px 5px 5px -3px, rgb(0 0 0 / 14%) 0px 8px 10px 1px, rgb(0 0 0 / 12%) 0px 3px 14px 2px',
  borderRadius: '4px',
  zIndex: 1300,
  minWidth: '200px',
  color: colorPalette.monotone[4],
})

const ProjectMemberSelectButton = styled(Button)({ margin: '0 10px 0 20px' })

const SelectedMemberNameTypography = styled(Typography)({
  fontSize: FontSize.XX_LARGE,
  fontWeight: FontWeight.BOLD,
  margin: '0px 10px 0 0',
})

const SelectedMemberAvatar = styled(Avatar)({
  margin: '0px 10px',
  width: 30,
  height: 30,
})

const ProjectMemberSelector = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  margin: '10px 10px 5px 10px',
  padding: '15px 15px 10px 15px',
})

const ProjectMemberSelectBox = styled(Box)<{ $isUserSelected: boolean }>(
  ({ $isUserSelected }) => ({
    border: $isUserSelected
      ? `1px solid ${colorPalette.yellow[7]}`
      : `1px solid ${colorPalette.monotone[3]}`,
    backgroundColor: $isUserSelected
      ? colorPalette.yellow[0]
      : colorPalette.monotone[1],
    width: 'fit-content',
    height: '60px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '5px',
  })
)

const SelectedProjectMemberDiv = styled('div')<{ $isUserSelected: boolean }>(
  ({ $isUserSelected }) => ({
    fontSize: FontSize.LARGE,
    color: $isUserSelected ? colorPalette.yellow[7] : colorPalette.monotone[5],
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    letterSpacing: '1px',
  })
)
